import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@shared/src/utils/cn";
import { Spinner } from "@shared/src/components/common/misc/Spinner";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap h-9 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // Standard button variants
        primary: "bg-button-primary-bg text-neutral-100 hover:bg-button-primary-hover",
        secondary: "bg-button-secondary-bg text-text-secondary hover:bg-button-secondary-hover",
        tertiary: "bg-button-tertiary-bg text-text-primaryinverse hover:bg-button-tertiary-hover",
        success: "bg-button-success-bg text-neutral-100  hover:bg-button-success-hover",
        danger: "bg-button-danger-bg text-neutral-100  hover:bg-button-danger-hover",
        outline: "bg-transparent border border-button-primary-bg text-text-primary hover:bg-button-primary-hover",
        input:
          "bg-surfaces-bg-secondary-darker border border-border-secondary rounded-2xl  transition-all duration-200",

        // Surface variants (for different UI contexts)
        "surface-primary": "bg-surfaces-bg-primary text-text-primary hover:bg-surfaces-bg-hover",
        "surface-secondary": "bg-surfaces-bg-secondary text-text-secondary hover:bg-surfaces-bg-hover",
        "surface-tertiary": "bg-surfaces-bg-tertiary text-text-primary hover:bg-surfaces-bg-hover",

        // Utility variants
        ghost: "hover:bg-surfaces-bg-hover text-text-secondary",
        "ghost-clean": "text-text-secondary hover:opacity-75",
      },
      size: {
        default: "py-2 px-4",
        sm: "py-4 px-6", // 2x usage - small pills
        md: "py-6 px-8", // combining py-6 px-6 + py-6 px-8
        lg: "py-7 px-8", // 22x usage - most common!
        xl: "py-8 px-8", // 18x usage - second most common
        "2xl": "py-6 px-10", // 4x usage - wide buttons
        square: "p-5", // 6x usage - equal padding
        cta: "p-7", // 5x usage - call-to-action style
        icon: "size-9", // for icon buttons using Button component
      },
      radius: {
        md: "rounded-md",
        "2xl": "rounded-2xl",
        full: "rounded-full",
      },
      width: {
        default: "", // natural width
        full: "w-full", // full width
        fit: "w-fit", // fit content
        flex: "flex-1", // flexible width
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
      radius: "full",
      width: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, radius, width, asChild = false, loading = false, disabled, children, ...props },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";

    // If using asChild, we can't add loading spinner as Slot expects single child
    if (asChild) {
      return (
        <Comp
          className={cn(buttonVariants({ variant, size, radius, width, className }))}
          ref={ref}
          disabled={disabled || loading}
          {...props}
        >
          {children}
        </Comp>
      );
    }

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, radius, width, className }))}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading && <Spinner size={12} />}
        {children}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
